/* globals document, Image */
import React, { useCallback, useReducer, useRef, useState } from 'react';
import { styled } from 'baseui';
import { Block } from 'baseui/block';
import { Modal, ModalBody } from 'baseui/modal';
import { CanvasImageRenderer } from './canvas-image-renderer.react';
import { ControlPanel } from './control-panel.react';
import { reducer } from './reducer';
import { reducer as selectionReducer } from './selection-reducer';
import { downloadFromUrl } from './utils';
type Props = {
  src: string;
  onSelect?: (base64ImageData: string) => Promise<void> | void;
  enableImageSelection?: boolean;
  fitParent?: boolean;
  hideAspectRatio?: boolean;
};
export type SelectionState = {
  enableSelection: boolean;
  showSelection: boolean;
  selection: {
    x: number;
    y: number;
    refX?: number;
    refY?: number;
    width: number;
    height: number;
  };
};
const initialSelectionState: SelectionState = {
  enableSelection: false,
  showSelection: false,
  selection: {
    x: 0,
    y: 0,
    refX: 0,
    refY: 0,
    width: 0,
    height: 0
  }
};
export type ImageRendererState = {
  scale: number;
  minScale: number;
  maxScale: number;
  offsets: {
    x: number;
    y: number;
  };
  enablePan: boolean;
  img: {
    width: number;
    height: number;
    rotation: number;
  };
  expanded: boolean;
};
const initialState: ImageRendererState = {
  scale: 1,
  minScale: 0.25,
  maxScale: 4,
  offsets: {
    x: 0,
    y: 0
  },
  enablePan: false,
  img: {
    width: 0,
    height: 0,
    rotation: 0
  },
  expanded: false
};
const Container = styled<typeof Block, {
  $expand: boolean;
  $selecting: boolean;
}>(Block, ({
  $theme,
  $expand,
  $selecting
}) => {
  return {
    position: 'relative',
    width: '100%',
    height: $expand ? '100%' : '500px',
    cursor: $selecting ? 'crosshair' : 'move'
  };
});
const SelectionOverlay = styled('svg', () => ({
  'pointer-events': 'none',
  width: '100%',
  height: '100%',
  position: 'absolute',
  'z-index': 3
}));
const SelectionRect = styled('rect', ({
  $theme
}) => ({
  fill: 'rgba(0,0,0,0.8)',
  stroke: $theme.colors.primaryB
}));
export function ImageRenderer(props: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [selectionState, dispatchSelectionAction] = useReducer(selectionReducer, initialSelectionState);
  return <React.Fragment>
      {/* @ts-expect-error todo(ts-migration) TS2322 Type '{ state: ImageRendererState; selectionState: SelectionState; dispatch: Dispatch<ActionData>; dispatchSelectionAction: Dispatch<...>; ... 4 more ...; hideAspectRatio?: boolean; }' is not assignab... */}
      <Content {...props} state={state} selectionState={selectionState} dispatch={dispatch} dispatchSelectionAction={dispatchSelectionAction} />
      <Modal focusLock={false} isOpen={state.expanded} onClose={() => {
      dispatch({
        type: 'RESET_EXPANDED'
      });
    }} closeable={false} overrides={{
      Root: {
        style: {
          // default is 'fixed' which causes modal to overflow parent if consumer is using layout system like f7-ux
          position: 'absolute'
        }
      },
      DialogContainer: {
        style: {
          position: 'absolute',
          width: '100%',
          height: '100%'
        }
      },
      Dialog: {
        style: {
          width: '100%',
          height: '100%',
          marginTop: 0,
          marginRight: 0,
          marginBottom: 0,
          marginLeft: 0
        }
      }
    }}>
        <ModalBody style={{
        height: '100%',
        margin: 0
      }}>
          {/* @ts-expect-error todo(ts-migration) TS2322 Type '{ state: ImageRendererState; selectionState: SelectionState; dispatch: Dispatch<ActionData>; dispatchSelectionAction: Dispatch<...>; ... 4 more ...; hideAspectRatio?: boolean; }' is not assignab... */}
          <Content {...props} state={state} selectionState={selectionState} dispatch={dispatch} dispatchSelectionAction={dispatchSelectionAction} />
        </ModalBody>
      </Modal>
    </React.Fragment>;
}
function Content({
  src,
  onSelect,
  state,
  selectionState,
  dispatch,
  dispatchSelectionAction,
  enableImageSelection,
  fitParent = false,
  hideAspectRatio = false
}) {
  const containerRef = useRef();
  const [imageData, setImageData] = useState('');
  const [canDoImageSelection, setCanDoImageSelection] = useState(false);
  function onImageLoaded(base64Data: string) {
    // Empty image's base64 is "data;,"
    if (base64Data.length > 6) {
      setCanDoImageSelection(true);
      setImageData(base64Data);
    }
  }
  const onActionClick = icon => {
    switch (icon) {
      case 'zoomIn':
        {
          dispatch({
            type: 'ON_ZOOM_IN'
          });
          break;
        }
      case 'zoomOut':
        {
          dispatch({
            type: 'ON_ZOOM_OUT'
          });
          break;
        }
      case 'fullscreen':
        {
          dispatch({
            type: 'ON_ZOOM_FIT'
          });
          break;
        }
      case 'rotate':
        {
          dispatch({
            type: 'ON_IMG_ROTATE'
          });
          break;
        }
      case 'expand':
      case 'collapse':
        {
          dispatch({
            type: 'ON_EXPAND_TOGGLE'
          });
          break;
        }
      case 'show':
        {
          dispatchSelectionAction({
            type: 'ON_OCR'
          });
          dispatch({
            type: 'ON_OCR'
          });
          break;
        }
      case 'download':
        {
          downloadFromUrl(src);
          break;
        }
    }
  };
  return <Container ref={containerRef} $expand={state.expanded || fitParent} $selecting={selectionState.enableSelection}>
      {selectionState.enableSelection && <SelectionOverlay>
          {selectionState.showSelection && <SelectionRect x={selectionState.selection.x} y={selectionState.selection.y} width={selectionState.selection.width} height={selectionState.selection.height} />}
        </SelectionOverlay>}
      <CanvasImageRenderer expanded={state.expanded} scale={state.scale} offsets={state.offsets} rotation={state.img.rotation} src={src} enableImageSelection={enableImageSelection} onReferenceMouseDown={e => {
      if (!selectionState.enableSelection) {
        dispatch({
          type: 'ON_MOUSE_DOWN',
          payload: {
            x: e.referenceX,
            y: e.referenceY
          }
        });
      } else {
        if (!containerRef.current) {
          return;
        }
        // @ts-expect-error todo(ts-migration) TS2339 Property 'getBoundingClientRect' does not exist on type 'never'.
        const {
          x,
          y
        } = containerRef.current.getBoundingClientRect();
        dispatchSelectionAction({
          type: 'ON_MOUSE_DOWN',
          payload: {
            x: (e.clientX || 0) - x,
            y: (e.clientY || 0) - y,
            refX: e.referenceX,
            refY: e.referenceY
          }
        });
      }
    }} onReferenceMouseUp={() => {
      dispatch({
        type: 'ON_MOUSE_UP'
      });
      if (selectionState.enableSelection) {
        if (onSelect) {
          const selectionX = selectionState.selection.x;
          const selectionY = selectionState.selection.y;
          const width = selectionState.selection.width;
          const height = selectionState.selection.height;
          const tempImg = new Image();
          tempImg.onload = () => {
            const tempCanvas = document.createElement('canvas');
            tempCanvas.height = height;
            tempCanvas.width = width;
            const tempCtx = tempCanvas.getContext('2d');
            // @ts-expect-error missing null check
            tempCtx.drawImage(tempImg, selectionX, selectionY, width, height, 0, 0, width, height);
            const imgData = tempCanvas.toDataURL();
            // Removing the identifier prefix.
            onSelect(imgData.substring(imgData.indexOf(',') + 1));
            dispatchSelectionAction({
              type: 'ON_MOUSE_UP'
            });
          };
          tempImg.src = imageData;
        }
      }
    }} onReferenceMouseMove={e => {
      {
        if (!selectionState.enableSelection) {
          dispatch({
            type: 'ON_MOUSE_MOVE',
            payload: {
              x: e.referenceX,
              y: e.referenceY,
              xMovement: e.referenceMovementX,
              yMovement: e.referenceMovementY
            }
          });
        } else {
          if (!selectionState.showSelection || !containerRef.current) {
            return;
          }
          // @ts-expect-error todo(ts-migration) TS2339 Property 'getBoundingClientRect' does not exist on type 'never'.
          const {
            x,
            y
          } = containerRef.current.getBoundingClientRect();
          dispatchSelectionAction({
            type: 'ON_MOUSE_MOVE',
            payload: {
              x: (e.clientX || 0) - x,
              y: (e.clientY || 0) - y
            }
          });
        }
      }
    }} onMouseWheelMove={(delta: number) => {
      if (delta > 0) {
        dispatch({
          type: 'ON_ZOOM_IN'
        });
      } else if (delta < 0) {
        dispatch({
          type: 'ON_ZOOM_OUT'
        });
      }
    }} onImageLoaded={useCallback(imageData => onImageLoaded(imageData), [])} hideAspectRatio={hideAspectRatio} />
      <ControlPanel onClick={onActionClick} expanded={state.expanded} showOcr={enableImageSelection === true && canDoImageSelection} downloadFile={true} />
    </Container>;
}